import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, runInAction } from 'mobx';
import { validateDateThatIsEqualOrAfter } from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { getCurrentDateISO } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { bpHistory } from 'global/bpHistory';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { InitiateProposalInput } from 'store/modules/initiate-quote/types';

type InitiateQuoteRfqFields = {
  responseDueDate: FormControl<string>;
};

export default class RFQStepController {
  public form: FormController<InitiateQuoteRfqFields>;
  private _initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  private _getProposalInput: () => InitiateProposalInput;

  public get isValid(): boolean {
    return this.form.basicFieldsValidationsPassed;
  }

  public resetStep(): void {
    this.form.resetForm();
  }

  // Create a quote for CUSTOM proposal experience
  public async completeStep(): Promise<void> {
    const { effectiveDate, zip, state, county, proposalName } = this._getProposalInput();
    const responseDueDate = this.form.fields.responseDueDate.value;

    await this._initiateQuoteFlowDelegate.createCartAndSaveProposal({
      effectiveDate,
      proposalName,
      rateSegment: 'CUSTOM',
      browsingCriteria: {
        effectiveDate,
        zipCode: zip,
        state,
        county,
        shortName: {},
      },
      // Pass only the additional fields that should be saved in the Enrollment service.
      initiateFormAnswers: {
        responseDueDate,
      },
    });
    runInAction(() => {
      bpHistory.push(
        // The RFQ flow is currently separated from the Quote flow on the FE.
        // Legacy RFQ pages still use a dedicated RFQ checklist, while the BE serves RFQ steps as part of the Quote checklist.
        //
        // TODO:
        // - Merge the RFQ flow into the Quote checklist.
        // - Add RFQ steps to the Quote checklist helper.
        // - Remove the dedicated RFQ checklist from the FE.
        // - Replace this custom redirect with this.initiateQuoteFlowDelegate.onCompleteRedirect() once both flows are unified.
        getRFQProposalChecklistURL({
          periodId: this._initiateQuoteFlowDelegate.quoteData.periodId,
          cartId: this._initiateQuoteFlowDelegate.quoteData.cartId,
        }),
      );
    });
  }

  constructor(initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate, getProposalInput: () => InitiateProposalInput) {
    this._initiateQuoteFlowDelegate = initiateQuoteFlowDelegate;
    this._getProposalInput = getProposalInput;
    this.form = FormController.create(
      {
        responseDueDate: new FormControl<string>('', {
          optional: false,
          customValidations: [
            {
              // Validation ensures the selected date is equal to or later than the current day.
              check: (value: string): boolean => validateDateThatIsEqualOrAfter(value, getCurrentDateISO()),
              msg: 'The date cannot be in the past',
            },
          ],
        }),
      },
      {},
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
